import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import i18n from './lib/i18n';

// Contexts
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import TopNavigation from './components/TopNavigation';
import SupabaseSetup from './components/SupabaseSetup';
import Login from './components/Login';
import CustomerLogin from './components/CustomerLogin';
import DemoLoader from './components/DemoLoader';

// Pages
import Panels from './pages/Panels';
import Domains from './pages/Domains';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Servers from './pages/Servers';
import CompanyPayments from './pages/CompanyPayments';
import Registrars from './pages/Registrars';
import Users from './pages/Users';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import DataManagement from './pages/DataManagement';
import Maps from './pages/Maps';
import Settings from './pages/Settings';
import DomainPrices from './pages/DomainPrices';
import HostingPrices from './pages/HostingPrices';
import RecurringPaymentsAnalysis from './pages/RecurringPaymentsAnalysis';
import CustomerDashboard from './pages/CustomerDashboard';
import Dashboard from './pages/Dashboard';
import AccountTracking from './pages/AccountTracking';
import Calendar from './pages/Calendar';
import MobileAccessKeys from './pages/MobileAccessKeys';
import Support from './pages/Support';
// Languages page removed - translations are now file-based
// Assets page removed

// Not: Artık backend server kullanmıyoruz, sadece Supabase kullanıyoruz
// axios.defaults.baseURL kaldırıldı

function App() {
  const [dbConnected, setDbConnected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showDemoLoader, setShowDemoLoader] = useState(false);
  const [isCustomerAuthenticated, setIsCustomerAuthenticated] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      setLoading(true);
      
      // config.js dosyasının yüklenmesini bekle (eğer varsa)
      // Script tag'i head'de olduğu için yüklenmesi için bekleme
      if (typeof window !== 'undefined') {
        let waitCount = 0;
        while (!window.APP_CONFIG && waitCount < 10) {
          await new Promise(resolve => setTimeout(resolve, 50));
          waitCount++;
        }
      }
      
      // Müşteri authentication kontrolü
      await checkCustomerAuthentication();
      
      // Admin authentication kontrolü
      await checkAuthentication();
      
      // Sonra database bağlantısını kontrol et
      await checkDatabaseConnection();
      
      // Auth state değişikliklerini dinle
      try {
        const { onAuthStateChange } = await import('./lib/auth');
        onAuthStateChange((event, session) => {
          if (event === 'SIGNED_OUT') {
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('supabase.auth.token');
          } else if (event === 'SIGNED_IN' && session) {
            checkAuthentication();
          }
        });
      } catch (error) {
        // Hata durumunda devam et
      }
      
      setLoading(false);
    };
    
    initializeApp();
  }, []);

  // dbConnected değiştiğinde tabloları kontrol et
  useEffect(() => {
    if (dbConnected === true) {
      checkIfTablesEmpty();
    }
  }, [dbConnected]);

  const checkDatabaseConnection = async () => {
    try {
      // Backend API kullanılıyorsa, Supabase kontrolü atlama
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      
      // API base URL varsa, backend kullanılıyor demektir - otomatik bağlı say
      if (apiBaseUrl) {
        // Backend health check
        try {
          const response = await fetch(`${apiBaseUrl.replace(/\/api$/, '')}/health`);
          if (response.ok) {
            setDbConnected(true);
            return true;
          }
        } catch {
          // Health check başarısız - bağlı say
          setDbConnected(true);
          return true;
        }
        setDbConnected(true);
        return true;
      }
      
      // Önce environment variable kontrolü yap
      let supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      let supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      // Eğer environment variable yoksa, runtime config.js'den dene
      if (!supabaseUrl && typeof window !== 'undefined' && window.APP_CONFIG) {
        const configUrl = window.APP_CONFIG.REACT_APP_SUPABASE_URL;
        // Boş string kontrolü yap
        if (configUrl && configUrl.trim() !== '') {
          supabaseUrl = configUrl;
        }
      }
      if (!supabaseAnonKey && typeof window !== 'undefined' && window.APP_CONFIG) {
        const configKey = window.APP_CONFIG.REACT_APP_SUPABASE_ANON_KEY;
        // Boş string kontrolü yap
        if (configKey && configKey.trim() !== '') {
          supabaseAnonKey = configKey;
        }
      }
      
      // Eğer hala yoksa, localStorage'dan dene
      if (!supabaseUrl) {
        supabaseUrl = localStorage.getItem('supabase_url');
      }
      if (!supabaseAnonKey) {
        supabaseAnonKey = localStorage.getItem('supabase_anon_key');
      }
      
      if (!supabaseUrl || !supabaseAnonKey) {
        // Supabase config yok - Backend API kullanılıyor olabilir, bağlı say
        console.warn('⚠️ Supabase config bulunamadı. Backend API kullanılıyor.');
        setDbConnected(true);
        return true;
      }
      
      // Supabase bağlantısını kontrol et
      const { getSupabaseClient } = await import('./lib/supabase');
      const supabase = await getSupabaseClient();
      
      if (!supabase) {
        setDbConnected(true);
        return true;
      }
      
      // Basit bir test sorgusu yap
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);
      
      if (error) {
        // Tablo yoksa bile bağlantı başarılı sayılabilir
        // PGRST116: relation does not exist
        // PGRST205: Could not find the table in the schema cache
        const errorCode = String(error.code || '').toUpperCase();
        const errorMessage = String(error.message || '').toLowerCase();
        
        const isTableNotFound =
          errorCode === 'PGRST116' ||
          errorCode === 'PGRST205' ||
          errorMessage.includes('relation') ||
          errorMessage.includes('does not exist') ||
          errorMessage.includes('schema cache') ||
          errorMessage.includes('could not find the table') ||
          errorMessage.includes('not found');
        
        if (isTableNotFound) {
          // Tablo yok ama bağlantı başarılı
          setDbConnected(true);
          return true;
        } else {
          // Gerçek bağlantı hatası - sadece gerçek hataları console'a yaz
          if (!errorCode.includes('PGRST') && !errorMessage.includes('table') && !errorMessage.includes('schema')) {
            console.error('Supabase bağlantı hatası:', error);
          }
          setDbConnected(true);
          return true;
        }
      } else {
        // Bağlantı başarılı
        setDbConnected(true);
        return true;
      }
    } catch (error) {
      console.error('Veritabanı bağlantı kontrolü hatası:', error);
      // Hata olsa bile bağlı say (backend kullanılıyor)
      setDbConnected(true);
      return true;
    }
  };

  const checkIfTablesEmpty = async () => {
    try {
      const { checkTablesEmpty } = await import('./lib/supabase');
      const result = await checkTablesEmpty();
      
      if (result.isEmpty === true) {
        // Tablolar boş, demo loader'ı göster
        setShowDemoLoader(true);
      } else {
        setShowDemoLoader(false);
      }
    } catch (error) {
      console.error('Tablolar kontrol edilirken hata:', error);
      // Hata durumunda demo loader'ı gösterme
      setShowDemoLoader(false);
    }
  };

  const checkAuthentication = async () => {
    try {
      // Local storage'dan direkt kontrol et
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      try {
        const user = JSON.parse(userStr);
        
        // Token'ı decode et ve kontrol et
        const sessionData = JSON.parse(atob(token));
        
        // Session geçerliliğini kontrol et (24 saat)
        const sessionAge = Date.now() - sessionData.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 saat
        
        if (sessionAge <= maxAge && user && user.id) {
          // Session geçerli
          setUser(user);
          setIsAuthenticated(true);
        } else {
          // Session süresi dolmuş veya geçersiz
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('supabase.auth.token');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (e) {
        // Token veya user geçersiz
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('supabase.auth.token');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const handleSetupComplete = () => {
    setDbConnected(true);
    // Setup tamamlandıktan sonra tabloları kontrol et
    checkIfTablesEmpty();
  };

  const handleDemoLoadComplete = () => {
    setShowDemoLoader(false);
    // Demo veriler yüklendikten sonra sayfayı yenile
    window.location.reload();
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const checkCustomerAuthentication = async () => {
    try {
      const { getCustomerSession } = await import('./lib/auth');
      const sessionResult = await getCustomerSession();
      
      if (sessionResult.success && sessionResult.customer) {
        setIsCustomerAuthenticated(true);
      } else {
        setIsCustomerAuthenticated(false);
      }
    } catch (error) {
      setIsCustomerAuthenticated(false);
    }
  };

  const handleCustomerLoginSuccess = () => {
    setIsCustomerAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      // Supabase Auth ile çıkış yap
      const { signOut } = await import('./lib/auth');
      await signOut();
    } catch (error) {
    }
    
    // Local storage'ı temizle
    localStorage.removeItem('user');
    localStorage.removeItem('supabase.auth.token');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <Loader className="mx-auto h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-300">Yükleniyor...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Veritabanı bağlı değilse Supabase setup sayfasını göster
  if (!dbConnected) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <SupabaseSetup onSetupComplete={handleSetupComplete} />
        </div>
      </ThemeProvider>
    );
  }

  // Tablolar boşsa demo loader'ı göster
  if (showDemoLoader) {
    return (
      <ThemeProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <DemoLoader onComplete={handleDemoLoadComplete} />
        </div>
      </ThemeProvider>
    );
  }

  // Ana uygulama
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#f87171',
                secondary: '#fff',
              },
            },
          }}
        />
        <Router basename={process.env.PUBLIC_URL || ''}>
        <Routes>
          {/* Müşteri Login Route */}
          <Route 
            path="/customer-login" 
            element={
              isCustomerAuthenticated ? (
                <Navigate to="/customer-panel" replace />
              ) : (
                <CustomerLogin onLoginSuccess={handleCustomerLoginSuccess} />
              )
            } 
          />
          
          {/* Müşteri Panel Route */}
          <Route 
            path="/customer-panel" 
            element={
              isCustomerAuthenticated ? (
                <CustomerDashboard />
              ) : (
                <Navigate to="/customer-login" replace />
              )
            } 
          />
          
          {/* Admin Login Route */}
          {!isAuthenticated ? (
            <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          ) : (
            /* Admin Routes */
            <Route path="*" element={
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
                {/* Top Navigation Bar */}
                <TopNavigation user={user} onLogout={handleLogout} />
                
                {/* Main Content Area */}
                <div className="flex-1 overflow-auto">
                  <div className="max-w-[95%] mx-auto">
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/panels" element={<Panels />} />
                    <Route path="/domains" element={<Domains />} />
                    <Route path="/customers" element={<Customers />} />
                    <Route path="/customers/:id" element={<CustomerDetail />} />
                    <Route path="/servers" element={<Servers />} />
                    <Route path="/company-payments" element={<CompanyPayments />} />
                    <Route path="/registrars" element={<Registrars />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/companies" element={<Companies />} />
                    <Route path="/companies/:id" element={<CompanyDetail />} />
                    <Route path="/maps" element={<Maps />} />
                    <Route path="/data-management" element={<DataManagement />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/domain-prices" element={<DomainPrices />} />
                    <Route path="/hosting-prices" element={<HostingPrices />} />
                    <Route path="/recurring-payments-analysis" element={<RecurringPaymentsAnalysis />} />
                    <Route path="/account-tracking" element={<AccountTracking />} />
                    <Route path="/calendar" element={<Calendar />} />
                    <Route path="/mobile-access-keys" element={<MobileAccessKeys />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </div>
                </div>
              </div>
            } />
          )}
        </Routes>
        </Router>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
